import { useEffect, useMemo, useState } from "react";
import {
  CATEGORY_BY_TYPE, OBS_TYPES, TYPE_LABEL, THREAT_LABEL, VERB_HEADING, VERB_LABEL,
  getJson, useJson, entityPath, ago, fmtDate, nf, plural, citationMap, hasProse,
  RichText, Chip, IdBadge, KevBadge, victimPath, ConfidenceBadge, sectionLabel, card, control, sectionForType, sectionHome, FEED_LABEL,
  typeLabel, FRAMEWORK_SHORT, FRAMEWORK_SITE, countryPath, ipPath,
} from "./shared";
import { RelationshipGraph } from "./Graph";
import { ChoroplethMap, BarList, ColumnChart, CampaignTimeline, continuousSeries } from "./Charts";
import { comparePath } from "./Compare";
import { DrillDrawer } from "../dash/Drilldown";
import { RuleTable } from "./Rules";
import { RansomNotes, Negotiations } from "./Victims";
import { StixExport } from "./StixExport";
import { StixBar, StixExportLink, queryOf } from "./Stix";
import { graphHash } from "../graph/handoff";
import { CvssPill } from "./Vulns";
import { ReportIocTable, ReportLinks, ReportedInTab } from "./Reports";
import { WalletsTab } from "./Wallets";
import { OpRow, Credit } from "./CyberOps";

// The politically relevant incidents EuRepoC attributes to this group (Geopolitics › Cyber Operations).
function CyberOpsTab({ ops, s, ui }) {
  const { B, fs } = ui;
  const all = `#/kb/cyber-ops?kind=all&group=${encodeURIComponent(s.id)}`;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <p style={{ fontSize: fs(13), color: B.textMuted, margin: 0, lineHeight: 1.6, maxWidth: "110ch" }}>
        {plural(ops.total, "politically relevant incident")} the European Repository of Cyber Incidents attributes to {s.name}
        {ops.conflicts.length ? `, part of ${ops.conflicts.map(c => `${c.key} (${nf(c.count)})`).join(", ")}` : ""}
        {ops.against.length ? `; against ${ops.against.map(c => c.label).join(", ")}` : ""}. Who made each attribution, and how confident it is, is on each incident's page.
      </p>
      <div style={{ ...card(ui), padding: "6px 18px 12px" }}>
        {ops.items.map(x => <OpRow key={x.id} x={x} ui={ui} />)}
        {ops.total > ops.items.length && <a href={all} style={{ display: "inline-block", marginTop: 10, fontSize: fs(12), color: B.accent }}>All {nf(ops.total)} in Cyber Operations →</a>}
      </div>
      <Credit credit={ops.credit} ui={ui} />
    </div>
  );
}

// Whole-row navigation: clicking anywhere on a row opens the entity, unless the click
// landed on something interactive inside it (a link, button, badge, input).
const rowNav = (href) => ({
  onClick: (e) => {
    if (!href || e.target.closest("a,button,input,select,label,[role=button]")) return;
    e.stopPropagation(); // a nested (dive-deeper) row wins over the row it sits in
    window.location.hash = href.replace(/^#/, "");
  },
  style: href ? { cursor: "pointer" } : {},
});

const MIN_CONF_OPTIONS = [
  { v: 0, l: "Any confidence" }, { v: 1, l: "Low and up" }, { v: 30, l: "Medium and up" }, { v: 70, l: "High only" },
];

// Bucket used by the relationship type filter.
function bucketOf(r) {
  if (r.aggregate?.victims || r.other?.kind === "victim") return "victims";
  if (r.other?.kind === "sector") return "sectors";
  if (r.aggregate || OBS_TYPES.has(r.other?.type)) return "indicators";
  const t = r.other?.type;
  if (t === "intrusion-set") return "groups";
  if (t === "campaign") return "campaigns";
  if (t === "malware" || t === "tool") return "software";
  if (t === "attack-pattern") return "techniques";
  if (t === "course-of-action") return "mitigations";
  if (t === "vulnerability") return "vulnerabilities";
  return "other";
}
const BUCKET_LABEL = { groups: "Groups", campaigns: "Campaigns", software: "Software", techniques: "Techniques", mitigations: "Mitigations", vulnerabilities: "Vulnerabilities", sectors: "Sectors", victims: "Victims", indicators: "Indicators", other: "Other" };

// ─── Indicator table (observables), used for list pages and the Indicators tab ───
export function ObservableTable({ ui, type, entity, initialFamily, emptyText }) {
  const { B, fs, TypeIcon, getConf } = ui;
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [minConf, setMinConf] = useState(0);
  const [sort, setSort] = useState("recent");
  const [family, setFamily] = useState(initialFamily || "");
  const [source, setSource] = useState("");
  const [threatType, setThreatType] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [via, setVia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => { setFamily(initialFamily || ""); }, [initialFamily]);
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 250); return () => clearTimeout(t); }, [q]);

  const baseUrl = useMemo(() => {
    const p = new URLSearchParams({ sort, minConf: String(minConf), limit: "50" });
    if (type) p.set("type", type);
    if (entity) p.set("entity", entity);
    if (debounced) p.set("q", debounced);
    if (family) p.set("family", family);
    if (source) p.set("source", source);
    if (threatType) p.set("threatType", threatType);
    return `/api/kb/observables?${p}`;
  }, [type, entity, debounced, minConf, sort, family, source, threatType]);

  useEffect(() => {
    let live = true;
    setLoading(true); setError("");
    getJson(`${baseUrl}&offset=0`)
      .then(d => { if (!live) return; setRows(d.items); setTotal(d.total); if (!family) setVia(d.via || []); })
      .catch(e => live && setError(e.message))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [baseUrl, family]);

  const more = () => {
    getJson(`${baseUrl}&offset=${rows.length}`).then(d => setRows(r => [...r, ...d.items])).catch(e => setError(e.message));
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by value, family or tag…" style={{ ...control(ui), flex: "1 1 220px", minWidth: 0 }} />
        <select value={minConf} onChange={e => setMinConf(Number(e.target.value))} style={control(ui)}>
          {MIN_CONF_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
        <select value={sort} onChange={e => setSort(e.target.value)} style={control(ui)}>
          <option value="recent">Sort: Last seen</option>
          <option value="confidence">Sort: Confidence</option>
          <option value="value">Sort: Value</option>
        </select>
        <select value={source} onChange={e => setSource(e.target.value)} style={control(ui)} aria-label="Source">
          <option value="">All sources</option>
          {Object.entries(FEED_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={threatType} onChange={e => setThreatType(e.target.value)} style={control(ui)} aria-label="Threat type">
          <option value="">All threat types</option>
          {Object.entries(THREAT_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <StixExportLink ui={ui} cap={2000} href={`/api/kb/stix/observables${queryOf({ type, entity, q: debounced, minConf: minConf || "", family, source, threatType })}`} />
      </div>
      {via.length > 1 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
          <span style={{ fontSize: fs(11), color: B.textDim }}>Via software:</span>
          <button onClick={() => setFamily("")} style={pill(ui, !family)}>All</button>
          {via.map(v => <button key={v.id} onClick={() => setFamily(v.id)} style={pill(ui, family === v.id)}>{v.name} <span style={{ color: B.textDim }}>{nf(v.count)}</span></button>)}
        </div>
      )}
      {error && <div style={{ color: "#dc2626", fontSize: fs(12), marginBottom: 8 }}>{error}</div>}
      <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 8 }}>{loading ? "Loading…" : `${nf(total)} indicator${total === 1 ? "" : "s"}`}</div>
      {!loading && rows.length === 0 ? (
        <div style={{ ...card(ui), padding: 18, color: B.textMuted, fontSize: fs(13) }}>{emptyText || "No indicators match."}</div>
      ) : (
        <div style={{ ...card(ui), overflow: "hidden" }}>
          {rows.map((o, i) => (
            <div key={o.id} onClick={rowNav(entityPath(o)).onClick} className="kb-row" style={{ cursor: "pointer", display: "flex", gap: 10, padding: "11px 14px", borderTop: i ? `1px solid ${B.border}` : "none", alignItems: "flex-start", flexWrap: "wrap" }}>
              <TypeIcon type={o.type} color={getConf(o.type).color} size={14} />
              <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                <a href={entityPath(o)} style={{ fontFamily: "monospace", fontSize: fs(12), color: B.text, textDecoration: "none", wordBreak: "break-all" }}>{o.value}</a>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 5, alignItems: "center" }}>
                  {o.families.map(f => <a key={f.id} href={`#/kb/e/${encodeURIComponent(f.id)}`} style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf("malware").color}>{f.name}</Chip></a>)}
                  {(o.publishedIn || []).map(r => <a key={r.id} href={`#/kb/e/${encodeURIComponent(r.id)}`} onClick={e => e.stopPropagation()} title="Published on Idle Hour" style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf("report").color}>From report: {r.title}</Chip></a>)}
                  {o.threatTypes.map(t => <Chip key={t} ui={ui}>{THREAT_LABEL[t] || t}</Chip>)}
                  {o.via && <span style={{ fontSize: fs(10), color: B.textDim }}>via {o.via.name}</span>}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5, marginLeft: "auto" }}>
                <ConfidenceBadge confidence={o.confidence} ui={ui} />
                <span style={{ fontSize: fs(10), color: B.textDim, whiteSpace: "nowrap" }}>{ago(o.lastSeen)} · {o.sources.map(s => FEED_LABEL[s] || s).join(", ")}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {rows.length < total && !loading && (
        <div style={{ textAlign: "center", marginTop: 14 }}>
          <button onClick={more} style={{ ...control(ui), cursor: "pointer", padding: "9px 18px" }}>Show more ({nf(total - rows.length)} left)</button>
        </div>
      )}
    </div>
  );
}

const pill = (ui, on) => ({
  fontSize: ui.fs(11), fontFamily: "'Satoshi', sans-serif", cursor: "pointer", borderRadius: 20, padding: "4px 11px",
  color: on ? ui.B.text : ui.B.textMuted, fontWeight: on ? 700 : 500,
  background: on ? ui.B.primary + "18" : ui.B.bgCard, border: `1px solid ${on ? ui.B.primary + "55" : ui.B.border}`,
});

// ─── Relationships ───

// The other end of a relationship: a link to its profile, or (for aggregates) to the Indicators tab.
function OtherLabel({ r, ui, onShowIndicators }) {
  const { B, fs, TypeIcon, getConf } = ui;
  const o = r.other || {};
  if (r.aggregate) {
    return (
      <button onClick={() => onShowIndicators?.(r.aggregate.familyId, r.aggregate)} style={{ background: "none", border: "none", padding: 0, cursor: onShowIndicators ? "pointer" : "default", color: B.accent, fontSize: fs(13), fontWeight: 700, fontFamily: "'Satoshi', sans-serif", textAlign: "left" }}>
        {o.name} {onShowIndicators ? "→" : ""}
      </button>
    );
  }
  const isObs = OBS_TYPES.has(o.type);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, minWidth: 0, flexWrap: "wrap" }}>
      <TypeIcon type={o.type} color={getConf(o.type).color} size={14} />
      <a href={entityPath(o)} style={{ fontSize: fs(isObs ? 12 : 13), fontWeight: 700, color: B.text, textDecoration: "none", fontFamily: isObs ? "monospace" : "inherit", wordBreak: isObs ? "break-all" : "normal" }}>
        {o.parentName ? <span style={{ color: B.textDim, fontWeight: 500 }}>{o.parentName}: </span> : null}{o.name}
      </a>
      <IdBadge id={o.attackId} ui={ui} />
      <KevBadge kev={o.kev} ui={ui} />
    </span>
  );
}

// Second-hop relationships of a neighbour, loaded on demand.
function DiveDeeper({ id, excludeId, ui, depth }) {
  const { B, fs } = ui;
  const { data, error, loading } = useJson(`/api/kb/entity/${encodeURIComponent(id)}/neighbors`);
  if (loading) return <div style={{ fontSize: fs(11), color: B.textDim, padding: "6px 0" }}>Loading…</div>;
  if (error) return <div style={{ fontSize: fs(11), color: "#dc2626", padding: "6px 0" }}>{error}</div>;
  const items = data.items.filter(r => r.other?.id !== excludeId);
  return (
    <div style={{ marginTop: 8, paddingLeft: 12, borderLeft: `2px solid ${B.border}` }}>
      <div style={{ fontSize: fs(10), color: B.textDim, marginBottom: 4 }}>Strongest relationships of {data.name}{data.total > items.length ? ` (${items.length} of ${nf(data.total)})` : ""}</div>
      {items.map((r, i) => <RelRow key={i} r={r} subject={data.name} ui={ui} depth={depth + 1} compact />)}
      <a href={`#/kb/e/${encodeURIComponent(id)}`} style={{ fontSize: fs(11), color: B.accent, textDecoration: "none" }}>Open {data.name} →</a>
    </div>
  );
}

function RelRow({ r, subject, ui, depth = 0, compact, onShowIndicators }) {
  const { B, fs } = ui;
  const [open, setOpen] = useState(false);
  const cites = useMemo(() => citationMap(r.references), [r.references]);
  const canDive = !r.aggregate && r.other?.id && !r.other?.kind && depth < 2;
  const verb = VERB_LABEL[r.verb] || r.verb;
  const sentence = r.direction === "out" ? <>{subject} <b style={{ color: B.textMuted }}>{verb}</b> …</> : <>… <b style={{ color: B.textMuted }}>{verb}</b> {subject}</>;

  const nav = rowNav(!r.aggregate && r.other?.id ? entityPath(r.other) : null);
  return (
    <div onClick={nav.onClick} className={nav.style.cursor ? "kb-row" : undefined} style={{ ...nav.style, padding: compact ? "7px 0" : "12px 16px", borderLeft: r.inferred && !compact ? `3px dashed ${B.borderLight}` : "none", opacity: r.inferred ? 0.92 : 1 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 240px", minWidth: 0 }}>
          {!compact && <div style={{ fontSize: fs(10), color: B.textDim, marginBottom: 3 }}>{sentence}</div>}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {compact && <span style={{ fontSize: fs(11), color: B.textDim }}>{(VERB_HEADING[r.direction]?.[r.verb] || verb).toLowerCase()}</span>}
            <OtherLabel r={r} ui={ui} onShowIndicators={onShowIndicators} />
            {r.via && <Chip ui={ui} title="This link is inferred through shared software">via {r.via.name}</Chip>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginLeft: "auto" }}>
          {r.source && !compact && <span style={{ fontSize: fs(10), color: B.textDim, whiteSpace: "nowrap" }}>{r.source}</span>}
          <ConfidenceBadge confidence={r.confidence} ui={ui} />
          {canDive && (
            <button onClick={() => setOpen(o => !o)} title="Show this entity's relationships here" style={{ background: "none", border: `1px solid ${B.border}`, borderRadius: 6, color: B.textMuted, cursor: "pointer", fontSize: fs(10), padding: "2px 7px", fontFamily: "'Satoshi', sans-serif", whiteSpace: "nowrap" }}>
              {open ? "Hide" : "Dive deeper"} {open ? "▴" : "▾"}
            </button>
          )}
        </div>
      </div>
      {!compact && r.note && <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 4 }}>{r.note}</div>}
      {!compact && hasProse(r.procedure) && <RichText text={r.procedure} citations={cites} ui={ui} style={{ fontSize: fs(12), marginTop: 5 }} />}
      {open && <DiveDeeper id={r.other.id} excludeId={null} ui={ui} depth={depth} />}
    </div>
  );
}

export function RelationshipsTab({ relationships, subject, ui, initialBucket, onShowIndicators }) {
  const { B, fs } = ui;
  const [bucket, setBucket] = useState(initialBucket || "");
  const [verb, setVerb] = useState("");
  const [minConf, setMinConf] = useState(0);
  const [showInferred, setShowInferred] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => { setBucket(initialBucket || ""); }, [initialBucket]);

  const filtered = relationships.filter(r =>
    (!bucket || bucketOf(r) === bucket)
    && (!verb || r.verb === verb)
    && (showInferred || !r.inferred)
    && (!minConf || r.aggregate || (r.confidence?.score ?? 100) >= minConf));

  const bucketCounts = {};
  for (const r of relationships) bucketCounts[bucketOf(r)] = (bucketCounts[bucketOf(r)] || 0) + 1;
  const verbCounts = {};
  for (const r of relationships) verbCounts[r.verb] = (verbCounts[r.verb] || 0) + 1;

  // Group as "<direction> <verb>", reported links before inferred ones.
  const groups = new Map();
  for (const r of filtered) {
    const key = `${r.inferred ? "z" : "a"}|${r.direction}|${r.verb}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  }
  const ordered = [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));

  return (
    <div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        <button onClick={() => setBucket("")} style={pill(ui, !bucket)}>All <span style={{ color: B.textDim }}>{relationships.length}</span></button>
        {Object.entries(bucketCounts).map(([k, n]) => <button key={k} onClick={() => setBucket(k)} style={pill(ui, bucket === k)}>{BUCKET_LABEL[k]} <span style={{ color: B.textDim }}>{n}</span></button>)}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16, alignItems: "center" }}>
        <select value={verb} onChange={e => setVerb(e.target.value)} style={control(ui)}>
          <option value="">All relationship types</option>
          {Object.entries(verbCounts).map(([v, n]) => <option key={v} value={v}>{VERB_LABEL[v] || v} ({n})</option>)}
        </select>
        <select value={minConf} onChange={e => setMinConf(Number(e.target.value))} style={control(ui)}>
          {MIN_CONF_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
        {relationships.some(r => r.inferred) && (
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: fs(12), color: B.textMuted, cursor: "pointer" }}>
            <input type="checkbox" checked={showInferred} onChange={e => setShowInferred(e.target.checked)} /> Show inferred links
          </label>
        )}
      </div>

      {ordered.length === 0 && <div style={{ ...card(ui), padding: 18, color: B.textMuted, fontSize: fs(13) }}>No relationships match these filters.</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {ordered.map(([key, rows]) => {
          const [inf, dir, v] = key.split("|");
          const limit = expanded[key] ? rows.length : 20;
          return (
            <div key={key}>
              <div style={{ ...sectionLabel(ui), marginBottom: 8, display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                <span>{inf === "z" ? "Inferred · " : ""}{VERB_HEADING[dir]?.[v] || v}</span>
                <span style={{ color: B.textMuted }}>{rows.length}</span>
                <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500, fontFamily: "monospace", color: B.textDim, fontSize: fs(10) }}>STIX: {v}</span>
              </div>
              <div style={{ ...card(ui), overflow: "visible" }}>
                {rows.slice(0, limit).map((r, i) => (
                  <div key={i} style={{ borderTop: i ? `1px solid ${B.border}` : "none" }}>
                    <RelRow r={r} subject={subject} ui={ui} onShowIndicators={onShowIndicators} />
                  </div>
                ))}
              </div>
              {rows.length > limit && (
                <button onClick={() => setExpanded(e => ({ ...e, [key]: true }))} style={{ ...control(ui), cursor: "pointer", marginTop: 8, padding: "6px 12px" }}>Show all {nf(rows.length)}</button>
              )}
            </div>
          );
        })}
      </div>
      {relationships.some(r => r.inferred) && (
        <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 16, lineHeight: 1.6 }}>
          Inferred links (dashed) chain two reported relationships through shared software. They're capped at Low confidence and are not attribution. <a href="#/kb/confidence" style={{ color: B.accent, textDecoration: "none" }}>How we score confidence →</a>
        </div>
      )}
    </div>
  );
}

// Techniques grouped under ATT&CK tactics, in kill-chain order.
function TechniquesTab({ relationships, tactics, ui }) {
  const { B, fs } = ui;
  const items = relationships.filter(r => r.other?.type === "attack-pattern" && r.verb !== "subtechnique-of");
  const byTactic = new Map((tactics || []).map(t => [t.name, []]));
  for (const r of items) for (const t of (r.other.tactics?.length ? r.other.tactics : ["Other"])) {
    if (!byTactic.has(t)) byTactic.set(t, []);
    byTactic.get(t).push(r);
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {[...byTactic.entries()].filter(([, list]) => list.length).map(([name, list]) => (
        <div key={name}>
          <div style={{ ...sectionLabel(ui), marginBottom: 8 }}>{name} <span style={{ color: B.textMuted }}>· {list.length}</span></div>
          <div style={{ ...card(ui) }}>
            {list.map((r, i) => (
              <div key={i} style={{ borderTop: i ? `1px solid ${B.border}` : "none" }}>
                <RelRow r={r} subject="" ui={ui} />
              </div>
            ))}
          </div>
        </div>
      ))}
      {items.length === 0 && <div style={{ fontSize: fs(13), color: B.textMuted }}>No techniques.</div>}
    </div>
  );
}

// ─── Targeting: victims (leak-site claims) or countries named in the description ───
function TargetingTab({ data, ui, onDrill }) {
  const { B, fs } = ui;
  const v = data.victimology;
  const g = data.geography;
  const box = { ...card(ui), padding: "16px 18px" };
  const unknownCountry = v ? v.total - Object.values(v.byCountry).reduce((a, b) => a + b, 0) : 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {v && (
        <>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ fontSize: fs(13), color: B.textMuted, flex: "1 1 320px", lineHeight: 1.6 }}>
              <b style={{ color: B.text }}>{nf(v.total)} victims</b> claimed on the group's leak site{v.groups.length > 1 ? ` (as ${v.groups.join(", ")})` : ""}, {fmtDate(v.first)} – {fmtDate(v.last)}. Leak-site posts are the criminals' own claims and aren't independently verified.
            </div>
            <ConfidenceBadge confidence={v.confidence} ui={ui} size="lg" />
          </div>
          <div style={box}>
            <div style={{ ...sectionLabel(ui), marginBottom: 10 }}>Claimed victims by country</div>
            <ChoroplethMap mode="count" values={v.byCountry} numeric={v.countryNumeric} names={v.countryNames} unit="victims" ui={ui}
              onSelect={(iso2, d) => onDrill(`Claimed victims in ${d.name || iso2}`, { dataset: "victims", match: { dim: "country", key: iso2 } })} />
            {unknownCountry > 0 && <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 8 }}>{nf(unknownCountry)} victims have no country recorded.</div>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 380px), 1fr))", gap: 16 }}>
            <div style={box}>
              <div style={{ ...sectionLabel(ui), marginBottom: 6 }}>Claimed victims by sector</div>
              <BarList rows={Object.entries(v.bySector).filter(([k]) => k !== "Unknown").map(([label, value]) => ({ label, value }))} unit="Victims" ui={ui}
                onSelect={(r) => onDrill(`Claimed victims: ${r.label}`, { dataset: "victims", match: { dim: "sector", key: r.label } })} />
              {v.bySector.Unknown ? <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 8 }}>{nf(v.bySector.Unknown)} with no sector recorded.</div> : null}
            </div>
            <div style={box}>
              <div style={{ ...sectionLabel(ui), marginBottom: 10, display: "flex", justifyContent: "space-between" }}>
                <span>Most recent claims</span>
                <button onClick={() => onDrill("All claimed victims", { dataset: "victims" })} style={{ background: "none", border: "none", color: B.accent, cursor: "pointer", fontSize: fs(11), padding: 0, fontFamily: "'Satoshi', sans-serif", textTransform: "none", letterSpacing: 0 }}>All {nf(v.total)} →</button>
              </div>
              {v.recent.slice(0, 12).map((x, i) => (
                <div key={i} style={{ display: "flex", gap: 10, padding: "6px 0", borderTop: i ? `1px solid ${B.border}` : "none", fontSize: fs(12), alignItems: "baseline" }}>
                  {x.vid
                    ? <a href={victimPath(x.vid)} style={{ flex: 1, minWidth: 0, color: B.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: "none", borderBottom: `1px dotted ${B.textDim}` }} title={x.title}>{x.title || "Unnamed"}</a>
                    : <span style={{ flex: 1, minWidth: 0, color: B.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={x.title}>{x.title || "Unnamed"}</span>}
                  <span style={{ color: B.textDim, whiteSpace: "nowrap" }}>{x.country || "—"} · {ago(x.date)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      {g && (
        <div style={box}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
            <div style={{ ...sectionLabel(ui), flex: 1 }}>Countries named in the ATT&CK description</div>
            <ConfidenceBadge confidence={g.mentions[0].confidence} ui={ui} />
          </div>
          <ChoroplethMap mode="mentions" mentions={g.mentions} ui={ui} />
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
            {g.mentions.map(m => (
              <div key={m.iso2 + m.kind} style={{ fontSize: fs(12), lineHeight: 1.55 }}>
                <a href={countryPath(m.iso2)} style={{ color: B.text, fontWeight: 700, textDecoration: "none" }}>{m.name}</a> <span style={{ color: B.textDim }}>· {m.kind === "origin" ? "attributed origin" : "named as a target"}</span>
                <div style={{ color: B.textMuted }}>"{m.sentence}"</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 12, lineHeight: 1.6 }}>
            Extracted automatically from the description text and classified by the wording around each country, so read the sentence. A country missing here only means the description doesn't name it.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Timeline: campaigns, victims per month, indicators per day ───
function monthLabel(k, short) {
  const [y, m] = k.split("-");
  const d = new Date(Date.UTC(+y, +m - 1, 1));
  return d.toLocaleDateString(undefined, short ? { month: "short", year: "2-digit", timeZone: "UTC" } : { month: "long", year: "numeric", timeZone: "UTC" });
}
function dayLabel(k, short) {
  return new Date(k + "T00:00:00Z").toLocaleDateString(undefined, short ? { month: "short", day: "numeric", timeZone: "UTC" } : { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
}

function TimelineTab({ timeline, name, ui, onDrill }) {
  const { B, fs } = ui;
  const box = { ...card(ui), padding: "16px 18px" };
  const victims = continuousSeries(timeline.victimsByMonth, "month");
  const indicatorDays = Object.keys(timeline.indicatorsByDay || {}).sort();
  // Fewer than 3 days of data isn't a trend — say it in words instead of drawing a one-bar chart.
  const indicators = indicatorDays.length >= 3 ? continuousSeries(timeline.indicatorsByDay, "day", 90) : [];
  const indicatorTotal = Object.values(timeline.indicatorsByDay || {}).reduce((a, b) => a + b, 0);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {timeline.campaigns.length > 0 && (
        <div style={box}>
          <div style={{ ...sectionLabel(ui), marginBottom: 4 }}>Campaigns</div>
          <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 12 }}>Operations in ATT&CK linked to {name}, from first to last seen.</div>
          <CampaignTimeline campaigns={timeline.campaigns} ui={ui} />
        </div>
      )}
      {victims.length > 0 && (
        <div style={box}>
          <div style={{ ...sectionLabel(ui), marginBottom: 4 }}>Claimed victims per month</div>
          <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 6 }}>From leak-site posts tracked by ransomware.live.</div>
          <ColumnChart points={victims} unit="victims" formatLabel={monthLabel} partialKey={new Date().toISOString().slice(0, 7)} ui={ui}
            onSelect={(p) => onDrill(`Claims in ${monthLabel(p.key)}`, { dataset: "victims", match: { dim: "month", key: p.key } })} />
        </div>
      )}
      {indicators.length > 0 && (
        <div style={box}>
          <div style={{ ...sectionLabel(ui), marginBottom: 4 }}>New indicators per day{timeline.indicatorsInferred ? " (inferred)" : ""}</div>
          <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 6 }}>
            {timeline.indicatorsInferred
              ? `First-reported dates of indicators for software ${name} uses. The tools are shared, so this isn't ${name}'s own activity.`
              : "First-reported dates in the abuse.ch feeds, last 90 days. The free feeds only cover recent activity."}
          </div>
          <ColumnChart points={indicators} unit="indicators" formatLabel={dayLabel} ui={ui}
            onSelect={(p) => onDrill(`Indicators first reported ${dayLabel(p.key)}`, { dataset: "indicators", match: { dim: "day", key: p.key } })} />
        </div>
      )}
      {indicatorDays.length > 0 && indicatorDays.length < 3 && (
        <div style={{ ...box, fontSize: fs(13), color: B.textMuted }}>
          {plural(indicatorTotal, "indicator")}{timeline.indicatorsInferred ? ` for software ${name} uses` : ""} first reported on {indicatorDays.map(d => fmtDate(d)).join(" and ")} — too few days to show a trend.
        </div>
      )}
      {timeline.attackCreated && <div style={{ fontSize: fs(11), color: B.textDim }}>Tracked in MITRE ATT&CK since {fmtDate(timeline.attackCreated)}.</div>}
    </div>
  );
}


// ─── Profile page ───

// A report published on Idle Hour with Create: who and when, and, in the operator's
// browser (where Create remembered the publish key), a way to take it down.
function PublishedNote({ rep, id, ui }) {
  const { B, fs, HP } = ui;
  const [key] = useState(() => { try { return localStorage.getItem("ih-publish-key") || ""; } catch { return ""; } });
  const [state, setState] = useState({ busy: false, error: "", gone: false });
  const p = rep.onIdleHour;
  const unpublish = async () => {
    if (!window.confirm("Unpublish this report? It leaves the Reports and its indicators leave the Indicators lists.")) return;
    setState({ busy: true, error: "", gone: false });
    try {
      const r = await fetch("/api/create/unpublish", { method: "POST", headers: { "Content-Type": "application/json", "X-Publish-Key": key }, body: JSON.stringify({ id }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `The server answered ${r.status}.`);
      setState({ busy: false, error: "", gone: true });
    } catch (e) { setState({ busy: false, error: e.message, gone: false }); }
  };
  return (
    <div style={{ ...card(ui), padding: "12px 16px", fontSize: fs(12), color: B.textMuted, lineHeight: 1.6, borderColor: B.primary + "55" }}>
      Written and published on Idle Hour with Create{p.by ? <> by <b style={{ color: B.text }}>{p.by}</b></> : ""}, {fmtDate(p.publishedAt)}{p.updatedAt && p.updatedAt !== p.publishedAt ? ` (updated ${fmtDate(p.updatedAt)})` : ""}. Its links are stated by the author, not inferred; its indicators are also in the Indicators lists, labelled with this report.
      {rep.categories?.length > 0 && <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>{rep.categories.map(c => <Chip key={c} ui={ui}>{c}</Chip>)}</div>}
      {key && !state.gone && <div style={{ marginTop: 8 }}><button onClick={unpublish} disabled={state.busy} style={{ ...HP.navBtn, padding: "5px 12px", fontSize: fs(11.5), color: "#dc2626", borderColor: "#dc262666" }}>{state.busy ? "Unpublishing…" : "Unpublish"}</button></div>}
      {state.gone && <div style={{ marginTop: 8, color: "#16a34a", fontWeight: 700 }}>Unpublished. <a href="#/kb/reports" style={{ color: B.accent }}>Back to Reports</a></div>}
      {state.error && <div style={{ marginTop: 8, color: "#dc2626" }}>{state.error}</div>}
    </div>
  );
}

export function KbEntity({ id, ui, onOpenBundle, initialTab }) {
  const { B, fs, HP, TypeIcon, getConf } = ui;
  const { data, error, loading } = useJson(`/api/kb/entity/${encodeURIComponent(id)}`);
  const [tab, setTab] = useState(initialTab || "overview");
  const [relBucket, setRelBucket] = useState("");
  const [indFamily, setIndFamily] = useState("");
  const [status, setStatus] = useState("");
  const [copied, setCopied] = useState("");
  const [drill, setDrill] = useState(null);

  useEffect(() => { setTab(initialTab || "overview"); setStatus(""); setRelBucket(""); setIndFamily(""); }, [id, initialTab]);

  const entity = data?.entity;
  const cites = useMemo(() => citationMap(entity?.external_references), [entity]);

  if (loading) return <div style={{ color: B.textDim, fontSize: fs(13), padding: 20 }}>Loading…</div>;
  if (error || !entity) return (
    <div style={{ padding: 24, ...card(ui), color: B.textMuted }}>
      {error === "Entity not found." ? "Nothing in the Knowledge Base matches this. Indicators only appear here while they're in the recent abuse.ch feeds." : error || "Not found."}{" "}
      <a href="#/kb" style={{ color: B.accent }}>Back to Knowledge Base</a>
    </div>
  );

  const { kind, summary: s, relationships } = data;
  const isObs = kind === "observable";
  const conf = getConf(entity.type);
  const cat = CATEGORY_BY_TYPE[entity.type];
  const techniqueCount = relationships.filter(r => r.other?.type === "attack-pattern" && r.verb !== "subtechnique-of").length;
  const indicatorCount = data.indicatorCount || data.inferredIndicatorCount || 0;

  const showIndicators = (familyId, agg) => { if (agg?.victims) { setTab("targeting"); return; } setIndFamily(familyId || ""); setTab("indicators"); };
  // Records behind a chart on this profile, always scoped to this entity.
  const drillFor = (title, params) => setDrill({ title: `${s.name} · ${title}`, params: { ...params, scope: { threats: [s.attackId || s.id] } } });
  const showRelationships = (bucket) => { setRelBucket(bucket || ""); setTab("relationships"); };
  const fromGraph = (bucket, familyId) => (bucket === "indicators" && indicatorCount ? showIndicators(familyId) : showRelationships(bucket));
  const hasTargeting = !!(data.victimology || data.geography);

  const tabs = [
    { k: "overview", l: "Overview" },
    { k: "relationships", l: "Relationships", n: relationships.length },
    relationships.length ? { k: "graph", l: "Graph" } : null,
    hasTargeting ? { k: "targeting", l: data.victimology ? "Victims" : "Geography", n: data.victimology?.total } : null,
    data.timeline ? { k: "timeline", l: "Timeline" } : null,
    techniqueCount && entity.type !== "attack-pattern" ? { k: "techniques", l: entity.type === "course-of-action" ? ({ d3fend: "Counters", engage: "Engages" }[s.framework] || "Mitigates") : "Techniques", n: techniqueCount } : null,
    indicatorCount ? { k: "indicators", l: data.inferredIndicatorCount ? "Indicators (inferred)" : "Indicators", n: indicatorCount } : null,
    entity.x_ih_detections?.length ? { k: "detections", l: "Detections", n: entity.x_ih_detections.length } : null,
    data.detections?.total ? { k: "rules", l: "Detection rules", n: data.detections.total } : null,
    data.ransom?.notes ? { k: "notes", l: "Ransom note", n: data.ransom.notes > 1 ? data.ransom.notes : undefined } : null,
    data.ransom?.chats ? { k: "negotiations", l: "Negotiations", n: data.ransom.chats } : null,
    data.wallets?.total || data.wallets?.treasury?.length ? { k: "wallets", l: "Wallets", n: data.wallets.total || undefined } : null,
    data.cyberOps ? { k: "cyberops", l: "Cyber operations", n: data.cyberOps.total } : null,
    kind !== "report" && data.reports?.length ? { k: "reports", l: "Reported in", n: data.reports.length } : null,
    cites.size && kind !== "report" ? { k: "references", l: "References", n: cites.size } : null,
    { k: "raw", l: "STIX 2.1" },
  ].filter(Boolean);

  const copy = (text, what) => {
    navigator.clipboard?.writeText(text).then(() => { setCopied(what); setTimeout(() => setCopied(""), 1500); });
  };

  const rep = data.report;
  const READ_FROM = { feed: "The full article, from the publisher's feed", page: "The full article, from the publisher's site", summary: "Only the feed's summary", published: "Written and published on Idle Hour with Create" };
  const meta = rep ? [
    ["Source", rep.source.name],
    ["Source reliability", `${rep.source.reliability} (${{ A: "completely reliable", B: "usually reliable", C: "fairly reliable", D: "not usually reliable" }[rep.source.reliability] || "unknown"})`],
    ["Published", fmtDate(rep.published)],
    rep.author && ["Author", rep.author],
    ["Read from", READ_FROM[rep.textFrom] || rep.textFrom],
  ] : isObs ? [
    ["First seen", fmtDate(s.firstSeen)],
    ["Last seen", fmtDate(s.lastSeen)],
    s.sources.length && ["Reported by", s.sources.map(x => FEED_LABEL[x] || x).join(", ")],
    s.publishedIn?.length && ["Published in", s.publishedIn.map(r => `“${r.title}”`).join("; ")],
    data.fileNames?.length && ["File names", data.fileNames.slice(0, 3).join(", ")],
    data.hostedUrlCount && ["Hosts", plural(data.hostedUrlCount, "malicious URL")],
  ] : [
    s.firstSeen && ["First seen", fmtDate(s.firstSeen)],
    s.lastSeen && ["Last seen", fmtDate(s.lastSeen)],
    s.platforms?.length && ["Platforms", s.platforms.join(", ")],
    s.tactics?.length && ["Tactics", s.tactics.join(", ")],
    data.feed?.lastSeen && ["Last in feeds", ago(data.feed.lastSeen)],
    kind === "attack" && entity.modified && ["Last updated", fmtDate(entity.modified)],
    data.nvd?.cvss && ["CVSS", `${data.nvd.cvss.score} ${data.nvd.cvss.severity || ""} (v${data.nvd.cvss.version})`],
    data.nvd?.published && ["Published", fmtDate(data.nvd.published + "T00:00:00Z")],
    !data.kev && data.nvd?.products?.length && ["Affects", data.nvd.products.slice(0, 3).map(p => `${p.vendor} ${p.product}`).join(", ") + (data.nvd.products.length > 3 ? ` +${data.nvd.products.length - 3}` : "")],
    !data.kev?.cwes?.length && data.nvd?.cwes?.length && ["Weakness", data.nvd.cwes.join(", ")],
    data.kev && ["Vendor", data.kev.vendor],
    data.kev && ["Product", data.kev.product],
    data.kev && ["Added to CISA KEV", fmtDate(data.kev.added + "T00:00:00Z")],
    data.kev?.cwes?.length && ["Weakness", data.kev.cwes.join(", ")],
    entity.x_ih_case_type && ["Case type", entity.x_ih_case_type === "exercise" ? "Red-team exercise or research" : entity.x_ih_case_type === "incident" ? "Real-world incident" : entity.x_ih_case_type],
    entity.x_ih_target && ["Target", entity.x_ih_target],
    entity.x_ih_actor && ["Actor", entity.x_ih_actor],
    entity.x_ih_engage?.goals?.length && ["Goal", entity.x_ih_engage.goals.join(", ")],
    entity.x_ih_engage?.kind && ["Kind", entity.x_ih_engage.kind === "Strategic" ? "Strategic (planning and review)" : "Engagement (in the operation)"],
    entity.x_ih_maturity && ["Maturity", { feasible: "Feasible (shown possible)", demonstrated: "Demonstrated (shown in research)", realized: "Realized (used in the wild)" }[entity.x_ih_maturity] || entity.x_ih_maturity.replace(/^./, c => c.toUpperCase())],
    entity.x_ih_capec?.likelihood && ["Likelihood", entity.x_ih_capec.likelihood],
    entity.x_ih_capec?.severity && ["Typical severity", entity.x_ih_capec.severity],
    entity.x_ih_capec?.abstraction && ["Abstraction", entity.x_ih_capec.abstraction],
    entity.x_ih_cwes?.length && ["Weaknesses", entity.x_ih_cwes.slice(0, 6).join(", ") + (entity.x_ih_cwes.length > 6 ? ` +${entity.x_ih_cwes.length - 6}` : "")],
    entity.x_ih_risk && ["Notional risk score", ["high", "medium", "low"].filter(l => entity.x_ih_risk[l] != null).map(l => `${l} ${entity.x_ih_risk[l]}`).join(" · ")],
  ];
  const section = sectionForType(entity.type);

  // Strongest reported relationships, as sentences, for the overview.
  const keyRels = relationships
    .filter(r => !r.inferred && r.verb !== "subtechnique-of" && !r.aggregate?.victims && r.other?.kind !== "victim" && (r.aggregate || r.confidence))
    .sort((a, b) => (b.aggregate ? 101 : b.confidence.score) - (a.aggregate ? 101 : a.confidence.score))
    .slice(0, 8);

  const statTiles = [
    ["groups", "Groups"], ["campaigns", "Campaigns"], ["software", "Software"], ["techniques", "Techniques"], ["mitigations", "Mitigations"], ["vulnerabilities", "Vulnerabilities"], ["sectors", "Sectors targeted"], ["victims", "Claimed victims"], ["indicators", "Indicators"],
  ].map(([b, l]) => {
    const rows = relationships.filter(r => bucketOf(r) === b);
    return { b, l, n: rows.reduce((sum, r) => sum + (r.aggregate?.count || 1), 0), inferred: rows.length > 0 && rows.every(r => r.inferred) };
  }).filter(t => t.n);

  return (
    <>
      <div style={{ fontSize: fs(12), color: B.textDim, marginBottom: 12 }}>
        <a href={section ? sectionHome(section) : "#/kb"} style={{ color: B.textDim, textDecoration: "none" }}>{section?.label || "Knowledge Base"}</a>
        <span> / </span>
        <a href={`#/kb/${cat?.slug || ""}`} style={{ color: B.textDim, textDecoration: "none" }}>{cat?.label}</a>
      </div>

      {/* Header */}
      <div style={{ ...card(ui), borderTop: `3px solid ${conf.color}`, padding: "20px 22px" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: conf.color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <TypeIcon type={entity.type} color={conf.color} size={22} />
          </div>
          <div style={{ flex: "1 1 260px", minWidth: 0 }}>
            <div style={{ fontSize: fs(11), color: conf.color, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {s.isSubtechnique ? "Sub-technique" : typeLabel(s)}
              {s.framework && s.framework !== "enterprise" && <Chip ui={ui}>{FRAMEWORK_SHORT[s.framework]}</Chip>}
              {kind === "family" && <Chip ui={ui}>{s.sigbase ? "signature-base · not in ATT&CK" : "Feeds only · not in ATT&CK"}</Chip>}
            </div>
            {isObs ? (
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start", margin: "4px 0 8px" }}>
                <h1 style={{ fontSize: fs(s.value.length > 40 ? 16 : 24), fontWeight: 800, margin: 0, color: B.text, fontFamily: "monospace", wordBreak: "break-all" }}>{s.value}</h1>
                <button onClick={() => copy(s.value, "value")} style={{ ...HP.navBtn, padding: "3px 9px", flexShrink: 0 }}>{copied === "value" ? "Copied ✓" : "Copy"}</button>
              </div>
            ) : (
              <h1 style={{ fontSize: fs(28), fontWeight: 900, margin: "2px 0 8px", color: B.text, wordBreak: "break-word" }}>
                {s.parentName ? <span style={{ color: B.textDim, fontWeight: 700 }}>{s.parentName}: </span> : null}{entity.name}
              </h1>
            )}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              {isObs && <ConfidenceBadge confidence={s.confidence} ui={ui} size="lg" />}
              <IdBadge id={s.attackId} ui={ui} />
              <KevBadge kev={s.kev} ui={ui} size="lg" />
              {kind === "vulnerability" && <CvssPill cvss={data.nvd?.cvss} ui={ui} size="lg" />}
              {(s.aliases || []).map(a => <Chip key={a} ui={ui}>{a}</Chip>)}
              {data.feed?.aliases?.filter(a => !(s.aliases || []).includes(a)).map(a => <Chip key={a} ui={ui} title="Alias used in the abuse.ch feeds / Malpedia">{a}</Chip>)}
              {isObs && s.families.map(f => <a key={f.id} href={`#/kb/e/${encodeURIComponent(f.id)}`} style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf("malware").color}>{f.name}</Chip></a>)}
              {isObs && s.threatTypes.map(t => <Chip key={t} ui={ui}>{THREAT_LABEL[t] || t}</Chip>)}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {isObs && s.type === "ipv4-addr" && <a href={ipPath(s.value)} title="What's open on this address, and whether it's scanning the internet" style={{ ...HP.navBtn, textDecoration: "none", display: "inline-flex", alignItems: "center" }}>Internet view</a>}
            <a href={graphHash(s.attackId || (kind === "vulnerability" ? s.name : s.id))} title="See this and everything linked to it as an interactive graph" style={{ ...HP.primaryBtn, padding: "8px 14px", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="6" cy="6" r="2.5" /><circle cx="18" cy="8" r="2.5" /><circle cx="10" cy="18" r="2.5" /><line x1="8.2" y1="7" x2="15.6" y2="7.6" /><line x1="7" y1="8.3" x2="9.2" y2="15.6" /><line x1="16.3" y1="10" x2="11.8" y2="16.2" /></svg>
              View in Graph
            </a>
            {rep && (!rep.onIdleHour || rep.onIdleHour.sourceUrl) && <a href={rep.onIdleHour?.sourceUrl || rep.link} target="_blank" rel="noreferrer" style={{ ...HP.navBtn, padding: "8px 14px", textDecoration: "none", display: "inline-flex", alignItems: "center" }}>Read the original ↗</a>}
            {s.url && kind === "vulnerability" && <a href={s.url} target="_blank" rel="noreferrer" style={{ ...HP.navBtn, padding: "8px 14px", textDecoration: "none", display: "inline-flex", alignItems: "center" }}>NVD ↗</a>}
            {s.url && kind === "attack" && <a href={s.url} target="_blank" rel="noreferrer" style={{ ...HP.navBtn, padding: "8px 14px", textDecoration: "none", display: "inline-flex", alignItems: "center" }}>{FRAMEWORK_SITE[s.framework] || "MITRE ATT&CK"} ↗</a>}
            {data.comparable && <a href={comparePath([s.attackId || s.id])} style={{ ...HP.navBtn, padding: "8px 14px", textDecoration: "none", display: "inline-flex", alignItems: "center" }}>Compare</a>}
            {data.feed?.ransomwareLive && <a href={data.feed.ransomwareLive} target="_blank" rel="noreferrer" style={{ ...HP.navBtn, padding: "8px 14px", textDecoration: "none", display: "inline-flex", alignItems: "center" }}>ransomware.live ↗</a>}
            {(data.feed?.malpedia) && <a href={data.feed.malpedia} target="_blank" rel="noreferrer" style={{ ...HP.navBtn, padding: "8px 14px", textDecoration: "none", display: "inline-flex", alignItems: "center" }}>Malpedia ↗</a>}
            {data.feed?.references?.[0] && <a href={data.feed.references[0]} target="_blank" rel="noreferrer noopener" style={{ ...HP.navBtn, padding: "8px 14px", textDecoration: "none", display: "inline-flex", alignItems: "center" }}>Report ↗</a>}
            {data.feed?.signatureBase && <a href={data.feed.signatureBase} target="_blank" rel="noreferrer noopener" style={{ ...HP.navBtn, padding: "8px 14px", textDecoration: "none", display: "inline-flex", alignItems: "center" }}>signature-base ↗</a>}
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px 22px", flexWrap: "wrap", marginTop: 16, paddingTop: 14, borderTop: `1px solid ${B.border}` }}>
          {meta.filter(Boolean).map(([k, v]) => (
            <div key={k} style={{ fontSize: fs(12) }}>
              <span style={{ color: B.textDim }}>{k}: </span><span style={{ color: B.text }}>{v}</span>
            </div>
          ))}
        </div>
        <StixBar apiKey={s.id} ui={ui} onCustomize={() => setTab("raw")} />
        {status && <div style={{ marginTop: 10, fontSize: fs(12), color: "#dc2626" }}>{status}</div>}
      </div>

      {kind === "ransomware" && (
        <div style={{ ...card(ui), marginTop: 14, padding: "12px 16px", fontSize: fs(12), color: B.textMuted, lineHeight: 1.6, borderColor: s.unreliable ? "#dc262666" : B.border }}>
          {s.unreliable
            ? "ransomware.live flags this group's victim claims as unreliable — many cannot be verified. Treat everything here as unconfirmed."
            : "This ransomware operation is tracked by ransomware.live but isn't in MITRE ATT&CK, so there's no technique or tooling data — only the victims it has claimed on its leak site."}
        </div>
      )}

      {kind === "vulnerability" && (
        <div style={{ ...card(ui), marginTop: 14, padding: "14px 18px", fontSize: fs(13), color: B.textMuted, lineHeight: 1.6, borderColor: data.kev?.ransomware ? "#dc262666" : B.border }}>
          {data.kev ? (
            <>
              <div style={{ color: B.text, fontWeight: 800, marginBottom: 4 }}>{data.kev.name}</div>
              <div>
                In CISA's Known Exploited Vulnerabilities catalog: CISA has reliable evidence it is being exploited in the wild.
                {data.kev.ransomware && <b style={{ color: "#dc2626" }}> CISA knows of ransomware campaigns using it.</b>}
              </div>
              {data.kev.action && <div style={{ marginTop: 8 }}><span style={{ color: B.textDim }}>What to do: </span>{data.kev.action}</div>}
              {data.kev.due && <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 6 }}>US federal agencies must fix it by {fmtDate(data.kev.due + "T00:00:00Z")}.</div>}
            </>
          ) : (
            <>
              <div style={{ fontSize: fs(12) }}>
                Not in CISA's Known Exploited Vulnerabilities catalog, so there's no confirmed exploitation in the wild yet. That doesn't mean it isn't exploited; check your vendor's advisory.
                {data.nvd?.status && <span style={{ color: B.textDim }}> NVD status: {data.nvd.status}.</span>}
              </div>
            </>
          )}
        </div>
      )}

      {isObs && s.contextOnly && (
        <div style={{ ...card(ui), marginTop: 14, padding: "12px 16px", fontSize: fs(12), color: B.textMuted, lineHeight: 1.6, borderColor: "#d9770655" }}>
          This {TYPE_LABEL[entity.type].toLowerCase()} wasn't reported directly — it's here because malicious URLs are hosted on it. Shared hosting, CDNs and file-sharing sites often host malware without being malicious themselves.
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 2, borderBottom: `1px solid ${B.border}`, margin: "18px 0 18px", overflowX: "auto" }}>
        {tabs.map(t => (
          <button key={t.k} onClick={() => { setTab(t.k); if (t.k === "relationships") setRelBucket(""); if (t.k === "indicators") setIndFamily(""); }} style={{ background: "none", border: "none", borderBottom: `2px solid ${tab === t.k ? B.primary : "transparent"}`, color: tab === t.k ? B.text : B.textDim, padding: "9px 14px", cursor: "pointer", fontSize: fs(13), fontWeight: tab === t.k ? 700 : 500, fontFamily: "'Satoshi', sans-serif", whiteSpace: "nowrap" }}>
            {t.l}{t.n !== undefined && <span style={{ marginLeft: 6, fontSize: fs(11), color: B.textDim }}>{nf(t.n)}</span>}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {statTiles.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
              {statTiles.map(t => (
                <button key={t.b} onClick={() => (t.b === "indicators" && indicatorCount ? showIndicators("") : t.b === "victims" && hasTargeting ? setTab("targeting") : showRelationships(t.b))} style={{ textAlign: "left", ...card(ui), borderRadius: 10, padding: "12px 14px", cursor: "pointer", fontFamily: "'Satoshi', sans-serif" }}>
                  <div style={{ fontSize: fs(22), fontWeight: 900, color: B.text }}>{nf(t.n)}</div>
                  <div style={{ fontSize: fs(11), color: B.textDim }}>{t.l}{t.inferred ? " (inferred)" : ""}</div>
                </button>
              ))}
            </div>
          )}

          {!isObs && entity.description && (
            <div style={{ ...card(ui), padding: "18px 20px" }}>
              <div style={{ ...sectionLabel(ui), marginBottom: 10 }}>Description</div>
              <RichText text={entity.description} citations={cites} ui={ui} />
            </div>
          )}

          {/* Framework-specific detail: CAPEC prerequisites and mitigations, EMB3D device properties and IEC 62443 mappings */}
          {[
            ["Prerequisites", entity.x_ih_capec?.prerequisites],
            ["Mitigations (CAPEC)", entity.x_ih_capec?.mitigations],
            ["Adversary weaknesses it uses", entity.x_ih_engage?.weaknesses],
            ["Preconditions", entity.x_ih_fight?.preconditions],
            ["Critical assets", entity.x_ih_fight?.assets],
            ["What the adversary gains", entity.x_ih_fight?.postconditions],
            ["Applies to devices with", entity.x_ih_properties?.map(p => `${p.name} (${p.category})`)],
            ["IEC 62443 requirements", entity.x_ih_iec62443],
          ].filter(([, list]) => list?.length).map(([title, list]) => (
            <div key={title} style={{ ...card(ui), padding: "16px 20px" }}>
              <div style={{ ...sectionLabel(ui), marginBottom: 8 }}>{title}</div>
              <ul style={{ margin: 0, paddingLeft: 18, color: B.textMuted, fontSize: fs(13), lineHeight: 1.6, maxWidth: "110ch" }}>
                {list.map((x, i) => <li key={i} style={{ marginBottom: 4 }}>{x}</li>)}
              </ul>
            </div>
          ))}

          {keyRels.length > 0 && (
            <div>
              <div style={{ ...sectionLabel(ui), marginBottom: 8, display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <span>Key relationships</span>
                <button onClick={() => showRelationships("")} style={{ background: "none", border: "none", color: B.accent, cursor: "pointer", fontSize: fs(11), padding: 0, fontFamily: "'Satoshi', sans-serif", textTransform: "none", letterSpacing: 0 }}>All {nf(relationships.length)} →</button>
              </div>
              <div style={{ ...card(ui) }}>
                {keyRels.map((r, i) => (
                  <div key={i} style={{ borderTop: i ? `1px solid ${B.border}` : "none" }}>
                    <RelRow r={{ ...r, procedure: null }} subject={s.name} ui={ui} onShowIndicators={showIndicators} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {rep && <ReportLinks derived={data.derived} ui={ui} stated={!!rep.onIdleHour} />}

          {rep?.onIdleHour && <PublishedNote rep={rep} id={s.id} ui={ui} />}

          {rep && !rep.onIdleHour && (
            <div style={{ ...card(ui), padding: "12px 16px", fontSize: fs(12), color: B.textMuted, lineHeight: 1.6, borderColor: rep.error ? "#d9770655" : B.border }}>
              Read by keyword rules, not AI, from {rep.textFrom === "summary" ? "the feed's summary only" : `the full article (${nf(rep.textLength)} characters)`}. Names are matched against the Knowledge Base's groups, campaigns and software, and ordinary-word names (like “Play” or “Royal”) only count next to words like “ransomware”. The summary above is the publisher's; read the original for the analysis.
              {rep.error && <div style={{ marginTop: 6, color: "#d97706" }}>{rep.error}</div>}
              {rep.categories?.length > 0 && <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>{rep.categories.map(c => <Chip key={c} ui={ui}>{c}</Chip>)}</div>}
            </div>
          )}

          {data.similar?.length > 0 && (
            <div>
              <div style={{ ...sectionLabel(ui), marginBottom: 8 }}>Similar groups (by techniques used)</div>
              <div style={{ ...card(ui), overflow: "hidden" }}>
                {data.similar.map((g, i) => (
                  <div key={g.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 16px", borderTop: i ? `1px solid ${B.border}` : "none", flexWrap: "wrap" }}>
                    <a href={entityPath(g)} style={{ fontSize: fs(13), fontWeight: 700, color: B.text, textDecoration: "none", flex: "1 1 160px" }}>{g.name}</a>
                    <span style={{ fontSize: fs(12), color: B.textMuted }}>{Math.round(g.similarity * 100)}% overlap · {g.sharedTechniques} shared techniques</span>
                    <a href={comparePath([s.attackId || s.id, g.attackId || g.id])} style={{ fontSize: fs(12), color: B.accent, textDecoration: "none" }}>Compare →</a>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 6 }}>Overlap in ATT&CK techniques (Jaccard). Common techniques make many groups look alike — a lead, not attribution.</div>
            </div>
          )}

          {isObs && data.sightings?.length > 0 && (
            <div>
              <div style={{ ...sectionLabel(ui), marginBottom: 8 }}>Reports ({data.sightings.length})</div>
              <div style={{ ...card(ui), overflow: "hidden" }}>
                {data.sightings.map((x, i) => (
                  <div key={i} style={{ display: "flex", gap: 12, padding: "10px 16px", borderTop: i ? `1px solid ${B.border}` : "none", flexWrap: "wrap", alignItems: "center", fontSize: fs(12) }}>
                    <span style={{ color: B.text, fontWeight: 700, minWidth: 110 }}>{x.source}</span>
                    <span style={{ color: B.textMuted }}>{THREAT_LABEL[x.threatType] || x.threatType || "—"}</span>
                    {x.family && <span style={{ color: getConf("malware").color }}>{x.family}</span>}
                    {x.port && <span style={{ color: B.textDim }}>port {x.port}</span>}
                    {x.status && <span style={{ color: x.status === "online" ? "#dc2626" : B.textDim }}>{x.status}</span>}
                    {x.compromised && <Chip ui={ui} title="ThreatFox marks this as a legitimate site that was compromised">compromised site</Chip>}
                    {x.setName && <span style={{ color: B.textMuted }}>listed under “{x.setName}”{x.note ? ` · ${x.note}` : ""}</span>}
                    {x.feedConfidence > 0 && <span style={{ color: B.textDim }}>{x.set ? `score ${x.feedConfidence}` : `reporter confidence ${x.feedConfidence}%`}</span>}
                    <span style={{ color: B.textDim, marginLeft: "auto" }}>{x.reporter ? `by ${x.reporter} · ` : ""}{ago(x.lastSeen || x.firstSeen)}</span>
                    {x.reference && <a href={x.reference} target="_blank" rel="noreferrer" style={{ color: B.accent, textDecoration: "none" }}>Report ↗</a>}
                  </div>
                ))}
              </div>
              {data.tags?.length > 0 && <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>{data.tags.map(t => <Chip key={t} ui={ui}>{t}</Chip>)}</div>}
            </div>
          )}
        </div>
      )}

      {tab === "relationships" && <RelationshipsTab relationships={relationships} subject={s.name} ui={ui} initialBucket={relBucket} onShowIndicators={indicatorCount || hasTargeting ? showIndicators : null} />}
      {tab === "graph" && (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
            <a href={graphHash(s.id)} style={{ ...HP.navBtn, padding: "6px 12px", fontSize: fs(12), textDecoration: "none" }} title="Open the full graph explorer: every link, layouts, and double-click to expand">Open in the full graph →</a>
          </div>
          <RelationshipGraph subject={s} relationships={relationships.filter(r => !r.other?.kind && !r.aggregate?.victims)} ui={ui} onShowBucket={fromGraph} />
        </>
      )}
      {tab === "cyberops" && data.cyberOps && <CyberOpsTab ops={data.cyberOps} s={s} ui={ui} />}
      {tab === "notes" && <RansomNotes id={s.attackId || s.id} ui={ui} />}
      {tab === "negotiations" && <Negotiations id={s.attackId || s.id} ui={ui} />}
      {tab === "targeting" && <TargetingTab data={data} ui={ui} onDrill={drillFor} />}
      {tab === "timeline" && <TimelineTab timeline={data.timeline} name={s.name} ui={ui} onDrill={drillFor} />}
      {drill && <DrillDrawer request={drill} ui={ui} onClose={() => setDrill(null)} />}
      <style>{`.kb-row:hover { background: ${B.primary}0d; }`}</style>
      {tab === "techniques" && <TechniquesTab relationships={relationships} tactics={data.tactics} ui={ui} />}
      {tab === "detections" && (
        <>
          <p style={{ fontSize: fs(12), color: B.textMuted, margin: "0 0 12px", lineHeight: 1.6, maxWidth: "110ch" }}>
            {s.framework === "fight"
              ? "FiGHT's suggested detections for this technique: what to watch in each 5G data source. They're analytic ideas, not ready-made signatures."
              : "SPARTA's suggested detections for this technique: behaviours to watch for in spacecraft and ground telemetry. They're analytic ideas, not ready-made signatures."}
          </p>
          <div style={{ ...card(ui) }}>
            {entity.x_ih_detections.map((d, i) => (
              <div key={i} style={{ padding: "12px 16px", borderTop: i ? `1px solid ${B.border}` : "none" }}>
                <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                  {d.id && <IdBadge id={d.id} ui={ui} />}
                  <span style={{ fontSize: fs(13), fontWeight: 700, color: B.text }}>{d.name}</span>
                </div>
                {d.description && <div style={{ fontSize: fs(12), color: B.textMuted, marginTop: 4, lineHeight: 1.55 }}>{d.description}</div>}
              </div>
            ))}
          </div>
        </>
      )}
      {tab === "rules" && (
        <>
          <p style={{ fontSize: fs(12), color: B.textMuted, margin: "0 0 12px", lineHeight: 1.6, maxWidth: "110ch" }}>
            Ready-made detection rules about {entity.name}{s.type === "attack-pattern" && !s.isSubtechnique ? " and its sub-techniques" : ""}: matched by {s.type === "vulnerability" ? "CVE" : s.type === "attack-pattern" ? "ATT&CK technique tag" : "ATT&CK ID or malware family name"} in each rule.
            {(s.type === "malware" || s.type === "tool") ? " Family matches come from rule names and metadata, so check a rule before deploying it." : ""}
          </p>
          <RuleTable ui={ui} params={{ entity: s.type === "vulnerability" ? s.name : s.attackId || s.id }} byEngine={data.detections.byEngine} />
        </>
      )}
      {tab === "reports" && <ReportedInTab reports={data.reports || []} subject={s} ui={ui} />}
      {tab === "wallets" && <WalletsTab wallets={data.wallets} subject={s.name} ui={ui} />}
      {tab === "indicators" && kind === "report" && <ReportIocTable iocs={data.iocs || []} ui={ui} vendor={data.report?.vendorFile} />}
      {tab === "indicators" && kind !== "report" && (
        <>
          {data.inferredIndicatorCount > 0 && (
            <div style={{ ...card(ui), padding: "12px 16px", fontSize: fs(12), color: B.textMuted, lineHeight: 1.6, marginBottom: 14 }}>
              These indicators belong to software that {entity.name} is known to use. The feeds don't say who operated them — most widely used tools (like Cobalt Strike) are shared by many groups — so every row is an inferred, low-confidence link, not attribution.
            </div>
          )}
          <ObservableTable ui={ui} entity={s.id} initialFamily={indFamily} />
        </>
      )}
      {tab === "references" && (
        <div style={{ ...card(ui), overflow: "hidden" }}>
          {[...cites.values()].map((r, i) => (
            <div key={r.source_name} style={{ display: "flex", gap: 12, padding: "11px 16px", borderTop: i ? `1px solid ${B.border}` : "none", fontSize: fs(12) }}>
              <span style={{ color: B.textDim, width: 28, flexShrink: 0, textAlign: "right" }}>[{r.n}]</span>
              <div style={{ minWidth: 0 }}>
                {r.url ? <a href={r.url} target="_blank" rel="noreferrer" style={{ color: B.accent, textDecoration: "none", wordBreak: "break-word" }}>{r.description || r.source_name}</a> : <span style={{ color: B.textMuted }}>{r.description || r.source_name}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {tab === "raw" && <StixExport id={s.id} entity={entity} ui={ui} onOpenBundle={onOpenBundle} />}
    </>
  );
}
